/**
 * The tab's workspace and its assets, in `sessionStorage` so each tab keeps its own.
 *
 * Deliberately NOT the NgRx sync meta-reducer: that serialises keys as
 * `semeris-${getEnvironment()}-${key}`, and `getEnvironment()` reads a bundled Angular constant.
 * `pre-fetch.js` runs before Angular exists and would have to reproduce that derivation — silently
 * degrading every reload to an empty scope if the two ever drift.
 */

/** Contract with `pre-fetch.js`, which reads this key before Angular boots. Keep both in step. */
export const TAB_WORKSPACE_KEY = 'semeris-tab-workspace';

export interface TabWorkspace {
  workspaceId: string;
  assets: string[];
}

export function writeTabWorkspace(scope: TabWorkspace): void {
  try {
    sessionStorage.setItem(TAB_WORKSPACE_KEY, JSON.stringify(scope));
  } catch {
    // Private-mode / storage-disabled: the tab still works, it just re-derives its scope on reload.
  }
}

export function readTabWorkspace(): TabWorkspace | null {
  try {
    const raw = sessionStorage.getItem(TAB_WORKSPACE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<TabWorkspace>) : null;

    return parsed?.workspaceId ? { workspaceId: parsed.workspaceId, assets: parsed.assets ?? [] } : null;
  } catch {
    return null;
  }
}

/**
 * The scope this tab claims: the URL's workspace, else the one it is switching to, else whatever
 * it last used, else nothing.
 *
 * Mirrors `claimedScopeHeaders()` in `pre-fetch.js`, which applies the same rule before Angular
 * exists — the two must agree, or the boot request and the store disagree about the workspace.
 *
 * The `/reload` leg matters as much as the URL one: a same-tab switch sits on `/reload` with no
 * `/ws/` segment while the store resets, and deferring to `sessionStorage` there would name the
 * workspace being left. `workspaceId` is that route's query param.
 *
 * A claim is made even with no assets to go with it: empty is narrow and self-correcting once the
 * workspace list lands, whereas deferring to the stored default would show another workspace's
 * data under this workspace's URL.
 */
export function resolveTabScope(): TabWorkspace | null {
  const fromUrl = /^\/ws\/([^/]+)/.exec(window.location.pathname)?.[1] || new URLSearchParams(window.location.search).get('workspaceId');
  const cached = readTabWorkspace();

  if (fromUrl) {
    return { workspaceId: fromUrl, assets: cached?.workspaceId === fromUrl ? cached.assets : [] };
  }

  return cached;
}
